import { graphHooks } from '@tunasong/graph-lib/react'
import { dayjs, shortUuid } from '@tunasong/models'
import type { ImportWorkflowOutput } from '@tunasong/redux'
import { storageApi, useCurrentUser, workflowsApi } from '@tunasong/redux'
import {
  Alert,
  Button,
  Divider,
  getEntityPathById,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@tunasong/ui-lib'
import type { ChangeEvent, FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import {
  describeImport,
  formatFileSize,
  importStatusLabel,
  maxUploadBytes,
  missingFileLabel,
  notALibrary,
} from './describe-import.js'
import styles from './imports.module.css'
import { uploadWithProgress } from './upload-with-progress.js'

/** How long to wait between status checks while a run is in flight, as Exports does */
const pollMs = 5000

/** How many missing items the result names before "Show all" */
const missingShown = 5

/**
 * What the action area shows (front-door spec §6.1): one state at a time. `started` hands over to the run the import
 * created, followed by its name, so the page never mistakes someone else's newest run for its own.
 */
type Phase =
  | { kind: 'idle' }
  | { kind: 'too-large'; size: number }
  | { kind: 'not-a-library' }
  | { kind: 'uploading'; file: File; loaded: number }
  | { kind: 'upload-failed'; file: File }
  | { kind: 'start-failed'; file: File }
  /** Uploaded; the import is being started */
  | { kind: 'starting'; file: File }
  | { kind: 'started'; file: File; executionName: string }

const isActive = (run: ImportWorkflowOutput) => run.status === 'RUNNING' || run.status === 'PENDING_REDRIVE'

const percentOf = (loaded: number, total: number) => (total > 0 ? Math.floor((loaded / total) * 100) : 0)

/** The import folder's name while it exists; whether it has been deleted since */
const useImportFolder = (folderId: string | null | undefined) => {
  const { entity, isError } = graphHooks.useEntity(folderId)
  return { name: entity?.name, deleted: Boolean(folderId) && (isError || Boolean(entity?.trash)) }
}

interface ImportResultProps {
  run: ImportWorkflowOutput
  file: File
  onRetry(file: File): void
}

/** The settled run's outcome, as one Alert that takes focus when it appears (spec §6.1, Accessibility) */
const ImportResult: FC<ImportResultProps> = ({ run, file, onRetry }) => {
  const navigate = useNavigate()
  const folder = useImportFolder(run.folderId)
  const described = describeImport({ run, folderName: folder.name })
  const [showAll, setShowAll] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (document.visibilityState === 'visible') {
      ref.current?.focus()
    }
  }, [])

  const folderLabel = folder.name ?? 'the import folder'
  const shown = showAll ? described.missing : described.missing.slice(0, missingShown)
  const { folderId } = run

  /** ui-lib's Alert takes no ref, so the focus target is the box around it; focus reads the Alert inside */
  return (
    <div ref={ref} tabIndex={-1} className={styles.result}>
      <Alert
        data-testid="import-result"
        role={described.severity === 'success' ? 'status' : 'alert'}
        severity={described.severity}
        className={styles.alert}
        action={
          <div className={styles.actions}>
            {described.canOpen && folderId && !folder.deleted ? (
              <Button
                variant="outlined"
                color="inherit"
                aria-label={`Open ${folderLabel}`}
                onClick={() => void navigate(getEntityPathById({ entityId: folderId }))}
              >
                Open folder
              </Button>
            ) : null}
            {described.canRetry ? (
              <Button
                variant="outlined"
                color="inherit"
                aria-label={`Try importing ${file.name} again`}
                onClick={() => onRetry(file)}
              >
                Try again
              </Button>
            ) : null}
          </div>
        }
      >
        {described.lines.map(line => (
          <Typography key={line} variant="body2">
            {line}
          </Typography>
        ))}
        {described.missing.length > 0 ? (
          <ul className={styles.missing}>
            {shown.map(item => (
              <li key={item.entityId}>
                <RouterLink to={getEntityPathById({ entityId: item.entityId })}>{missingFileLabel(item)}</RouterLink>
              </li>
            ))}
          </ul>
        ) : null}
        {described.missing.length > missingShown ? (
          <Button variant="text" color="inherit" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show fewer' : `Show all ${described.missing.length}`}
          </Button>
        ) : null}
      </Alert>
    </div>
  )
}

/** One past or in-flight run in the history list, newest first */
const ImportRun: FC<{ run: ImportWorkflowOutput }> = ({ run }) => {
  const navigate = useNavigate()
  const folder = useImportFolder(run.folderId)
  const started = dayjs(run.startDate).format('D MMM YYYY, HH:mm')
  const { folderId } = run
  return (
    <ListItem className={styles.row}>
      <ListItemText primary={folder.name ?? 'Library import'} secondary={`${importStatusLabel(run)} · ${started}`} />
      <div className={styles.rowAction}>
        {folder.deleted ? <Typography variant="body2">Folder deleted</Typography> : null}
        {folderId && !folder.deleted && folder.name ? (
          <Button
            variant="outlined"
            color="primary"
            aria-label={`Open ${folder.name}`}
            onClick={() => void navigate(getEntityPathById({ entityId: folderId }))}
          >
            Open
          </Button>
        ) : null}
      </div>
    </ListItem>
  )
}

/**
 * Profile → Imports, on the cloud (front-door spec §6): bring in a library downloaded from a local Tunasong. The file
 * is PUT to the caller's own import area with progress, the import starts on that key, and the page follows the run
 * it started until it settles. Everything lands in one new folder.
 */
export const Imports: FC = () => {
  const { userId } = useCurrentUser()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [announcement, setAnnouncement] = useState('')
  const [getUploadUrl] = storageApi.useLazyGetUploadUrlQuery()
  const [startImport] = workflowsApi.useImportSpaceMutation()
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  /** The last quarter announced, so progress speaks at 25, 50 and 75% only */
  const quarterRef = useRef(0)
  const messageId = useId()

  const [pollingInterval, setPollingInterval] = useState(0)
  const { currentData: runs = [] } = workflowsApi.useGetImportsQuery(undefined, {
    pollingInterval,
    skipPollingIfUnfocused: true,
  })

  const ownRun = phase.kind === 'started' ? runs.find(run => run.name === phase.executionName) : undefined
  const waitingForOwnRun = phase.kind === 'starting' || (phase.kind === 'started' && (!ownRun || isActive(ownRun)))
  const anyActive = runs.some(isActive)
  const shouldPoll = waitingForOwnRun || anyActive
  useEffect(() => setPollingInterval(shouldPoll ? pollMs : 0), [shouldPoll])

  const settled = phase.kind === 'started' && ownRun && !isActive(ownRun) ? ownRun : undefined
  const settledLine = settled ? describeImport({ run: settled, folderName: undefined }).lines[0] : undefined
  useEffect(() => {
    if (settledLine) {
      setAnnouncement(settledLine)
    }
  }, [settledLine])

  const uploading = phase.kind === 'uploading'
  useEffect(() => {
    if (!uploading) {
      return undefined
    }
    /** Closing the tab cancels the upload (spec §6.1, Uploading) */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [uploading])

  /**
   * One import at a time. Never derived from the mutation's `isSuccess`, which never resets (spec §6.1): the page's
   * own run is followed by name until the list shows it settled.
   */
  const busy = uploading || waitingForOwnRun || anyActive

  const handleUpload = async (file: File) => {
    const key = `users/${userId}/workflows/import-space/${shortUuid()}.zip`
    const controller = new AbortController()
    abortRef.current = controller
    quarterRef.current = 0
    setPhase({ kind: 'uploading', file, loaded: 0 })
    setAnnouncement('Upload started.')
    try {
      const { url } = await getUploadUrl({ filename: key, contentType: 'application/zip' }).unwrap()
      await uploadWithProgress({
        url,
        file,
        signal: controller.signal,
        onProgress: loaded => {
          setPhase({ kind: 'uploading', file, loaded })
          const quarter = Math.floor(percentOf(loaded, file.size) / 25)
          if (quarter > quarterRef.current && quarter < 4) {
            quarterRef.current = quarter
            setAnnouncement(`Uploaded ${quarter * 25}%.`)
          }
        },
      })
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        setPhase({ kind: 'idle' })
        setAnnouncement('Upload cancelled.')
        return
      }
      setPhase({ kind: 'upload-failed', file })
      setAnnouncement('The upload stopped before it finished.')
      return
    }
    setPhase({ kind: 'starting', file })
    setAnnouncement('Upload finished. Importing your library.')
    try {
      const { executionName } = await startImport({
        zipStorageKey: key,
        /** The folder is named in the uploader's own time (spec §6.1) */
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).unwrap()
      setPhase({ kind: 'started', file, executionName })
    } catch {
      setPhase({ kind: 'start-failed', file })
      setAnnouncement("The import couldn't start.")
    }
  }

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    /** Cleared, so choosing the same file again still fires a change */
    event.target.value = ''
    if (!file) {
      return
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setPhase({ kind: 'not-a-library' })
      return
    }
    if (file.size > maxUploadBytes) {
      setPhase({ kind: 'too-large', size: file.size })
      return
    }
    void handleUpload(file)
  }

  const handleRetry = (file: File) => {
    void handleUpload(file)
  }

  const describedBy = phase.kind === 'too-large' || phase.kind === 'not-a-library' ? messageId : undefined

  return (
    <div className={styles.imports}>
      <div role="status" data-testid="import-announcer" className={styles.announcer}>
        {announcement}
      </div>
      <Typography variant="body2">
        Bring in a library from a local Tunasong. In the local app, open Profile → Exports and choose Download library,
        then choose that file here. Everything lands in one new folder, and deleting that folder undoes the import.
      </Typography>
      <input ref={inputRef} type="file" accept=".zip" hidden onChange={handleFile} />
      <Button
        variant="contained"
        color="primary"
        className={styles.primary}
        disabled={busy}
        aria-describedby={describedBy}
        onClick={() => inputRef.current?.click()}
      >
        Choose library file…
      </Button>
      {phase.kind === 'too-large' ? (
        <Typography id={messageId} variant="body2" color="error">
          {`This file is ${formatFileSize(phase.size)}. Imports are limited to 5 GB for now. Nothing was uploaded.`}
        </Typography>
      ) : null}
      {phase.kind === 'not-a-library' ? (
        <Typography id={messageId} variant="body2" color="error">
          {notALibrary}
        </Typography>
      ) : null}
      {phase.kind === 'uploading' ? (
        <div className={styles.progress}>
          <LinearProgress
            variant="determinate"
            value={percentOf(phase.loaded, phase.file.size)}
            aria-label="Upload progress"
          />
          <Typography variant="body2">
            {`Uploading ${phase.file.name} — ${formatFileSize(phase.loaded)} of ${formatFileSize(phase.file.size)} (${percentOf(phase.loaded, phase.file.size)}%). Keep this tab open: closing it cancels the upload.`}
          </Typography>
          <Button variant="outlined" color="primary" onClick={() => abortRef.current?.abort()}>
            Cancel upload
          </Button>
        </div>
      ) : null}
      {phase.kind === 'upload-failed' || phase.kind === 'start-failed' ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              variant="outlined"
              color="inherit"
              aria-label={`Try importing ${phase.file.name} again`}
              onClick={() => handleRetry(phase.file)}
            >
              Try again
            </Button>
          }
        >
          {phase.kind === 'upload-failed'
            ? 'The upload stopped before it finished. Nothing was imported.'
            : "The import couldn't start. Nothing was imported."}
        </Alert>
      ) : null}
      {waitingForOwnRun ? (
        <div className={styles.progress}>
          <LinearProgress aria-label="Import progress" />
          <Typography variant="body2">
            Importing your library. This usually takes a few minutes. You can leave this page; the result appears here
            when it&apos;s done.
          </Typography>
        </div>
      ) : null}
      {settled && phase.kind === 'started' ? (
        <ImportResult key={settled.name} run={settled} file={phase.file} onRetry={handleRetry} />
      ) : null}
      <Divider>Imports</Divider>
      {runs.length === 0 ? (
        <Typography variant="body2">No imports yet.</Typography>
      ) : (
        <List aria-label="Imports">
          {runs.map(run => (
            <ImportRun key={run.name} run={run} />
          ))}
        </List>
      )}
    </div>
  )
}

export default Imports
